import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Database, RefreshCw, AlertCircle, Calendar, Hash, Clock } from 'lucide-react';
import { useApi } from '../hooks/useApi';

// Columns frozen to the left of the horizontal scroll, in display order.
// `width` doubles as the left offset of the next one.
const STICKY_COLUMNS = [
  { name: 'date', width: 100 },
  { name: 'hour', width: 60 },
  { name: 'Fix I', width: 90 },
  { name: 'Fix II', width: 90 },
];

const STICKY_LEFT = STICKY_COLUMNS.reduce((acc, col, i) => {
  acc[col.name] = STICKY_COLUMNS.slice(0, i).reduce((sum, c) => sum + c.width, 0);
  return acc;
}, {});

const STICKY_WIDTH = STICKY_COLUMNS.reduce((acc, col) => {
  acc[col.name] = col.width;
  return acc;
}, {});

// Only the rightmost frozen column casts the divider shadow. One shadow per
// sticky column stacks four translucent smudges between the frozen columns.
const LAST_STICKY = STICKY_COLUMNS[STICKY_COLUMNS.length - 1].name;

const EDGE_SHADOW = '2px 0 5px -2px rgba(0,0,0,0.1)';
const UNDER_SHADOW = '0 2px 5px -2px rgba(0,0,0,0.1)';

// Sized so at least MIN_VISIBLE_ROWS rows are on screen before the body has to
// scroll; the viewport expression takes over on taller windows.
const ROW_HEIGHT_PX = 37; // px-4 py-2 + text-sm line-height + 1px divider
const HEADER_HEIGHT_PX = 41;
const FOOTER_HEIGHT_PX = 45;
const MIN_VISIBLE_ROWS = 12;
const MIN_TABLE_HEIGHT_PX =
  MIN_VISIBLE_ROWS * ROW_HEIGHT_PX + HEADER_HEIGHT_PX + FOOTER_HEIGHT_PX;

// Presets count days of history ending today; the day-ahead row is always
// appended on top of them. Custom mode takes the two dates literally.
const RANGE_PRESETS = [
  { value: '3', label: 'Day-ahead + last 3 days' },
  { value: '7', label: 'Day-ahead + last 7 days' },
  { value: '14', label: 'Day-ahead + last 14 days' },
  { value: '30', label: 'Day-ahead + last 30 days' },
  { value: 'custom', label: 'Custom range…' },
];

// Local calendar date, not toISOString() - that shifts to UTC and can hand the
// picker yesterday's date late in the Warsaw evening.
const toDateInput = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const Data = () => {
  const { fetchGet } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [metadata, setMetadata] = useState({});
  const [addOneHour, setAddOneHour] = useState(true);

  const [rangeMode, setRangeMode] = useState('3');
  const [customFrom, setCustomFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 2);
    return toDateInput(d);
  });
  // Defaults to the day-ahead date, matching what the presets return.
  const [customTo, setCustomTo] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return toDateInput(d);
  });

  // Returns null while a custom range is incomplete, which also disables Load.
  const buildQuery = useCallback(() => {
    if (rangeMode !== 'custom') return `?days=${rangeMode}`;
    if (!customFrom || !customTo) return null;
    return `?date_from=${customFrom}&date_to=${customTo}`;
  }, [rangeMode, customFrom, customTo]);

  const fetchData = useCallback(async (queryOverride) => {
    const query = queryOverride || buildQuery();
    if (!query) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetchGet(`/data${query}`);
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const result = await response.json();
      if (result.status === 'success') {
        setMetadata({
          rowsReturned: result.rows_returned,
          uniqueDates: result.unique_dates,
          dateRange: result.date_range,
          cutoff: result.cutoff,
          today: result.today,
        });
        setData({ headers: result.columns, rows: result.rows });
      } else {
        setError(result.message || 'Failed to load data');
        setData(null);
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchGet, buildQuery]);

  // Presets load as soon as they are picked. A custom range deliberately does
  // NOT: selecting "Custom range…" used to fire a query straight away for
  // whatever dates happened to be sitting in the inputs — dates the user never
  // chose, on a request that can take tens of seconds. Custom waits for Load.
  const fetchDataRef = useRef(fetchData);
  useEffect(() => {
    fetchDataRef.current = fetchData;
  });

  useEffect(() => {
    if (rangeMode === 'custom') return;
    fetchDataRef.current(`?days=${rangeMode}`);
  }, [rangeMode]);

  // Rows dated after today are the day-ahead: forecasts are settled but the TGE
  // auctions have not cleared, so their fixings are legitimately empty.
  const dateIndex = useMemo(
    () => (data ? data.headers.indexOf('date') : -1),
    [data]
  );
  const isDayAheadRow = useCallback(
    (row) =>
      metadata.today && dateIndex !== -1 && row[dateIndex] > metadata.today,
    [metadata.today, dateIndex]
  );
  const dayAheadDates = useMemo(() => {
    if (!data || !metadata.today || dateIndex === -1) return [];
    return [...new Set(data.rows.map((r) => r[dateIndex]))].filter(
      (d) => d > metadata.today
    );
  }, [data, metadata.today, dateIndex]);

  const formatNumber = (value, columnName) => {
    if (value === null || value === undefined || value === '') {
      // An unsettled auction is not the same as a hole in the data.
      return columnName === 'Fix I' || columnName === 'Fix II' ? '—' : '';
    }
    if (columnName === 'date' || columnName === 'hour') return value;
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num % 1 === 0 ? num.toString() : num.toFixed(2);
  };

  const getColumnStyles = (columnName) => {
    if (columnName === 'date') return 'font-medium text-gray-900';
    if (columnName === 'hour') return 'text-center font-medium';
    if (columnName === 'Fix I') return 'text-right font-semibold text-indigo-600';
    if (columnName === 'Fix II') return 'text-right font-semibold text-rose-600';
    if (columnName.includes('price')) return 'text-right text-blue-600';
    if (columnName.includes('wind') || columnName.includes('solar')) return 'text-right text-green-600';
    if (columnName.includes('load')) return 'text-right text-purple-600';
    return 'text-right';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Historical Data</h2>
          <p className="text-gray-600 mt-1">
            Hourly market data from the feature store
          </p>

          {/* Metadata */}
          {data && (
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <Hash className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">
                  <span className="font-medium">{metadata.rowsReturned}</span> rows ·{' '}
                  <span className="font-medium">{metadata.uniqueDates}</span> days
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">
                  Date range: <span className="font-medium">{metadata.dateRange}</span>
                </span>
              </div>
              {metadata.cutoff && (
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">
                    Forecast cutoff: <span className="font-medium">{metadata.cutoff}</span>
                  </span>
                </div>
              )}
              {dayAheadDates.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-indigo-600" />
                  <span className="text-gray-600">
                    Day-ahead{' '}
                    <span className="font-semibold text-indigo-600">
                      {dayAheadDates.join(', ')}
                    </span>{' '}
                    — <span className="text-gray-400">—</span> marks an auction that has not settled
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-700">Display hours as 1-24</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={addOneHour}
              onChange={(e) => setAddOneHour(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* Range controls */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Time range
          </label>
          <select
            value={rangeMode}
            onChange={(e) => setRangeMode(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {RANGE_PRESETS.map((preset) => (
              <option key={preset.value} value={preset.value}>
                {preset.label}
              </option>
            ))}
          </select>
        </div>

        {rangeMode === 'custom' && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                From
              </label>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                To
              </label>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </>
        )}

        <button
          onClick={() => fetchData()}
          disabled={loading || !buildQuery()}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>{rangeMode === 'custom' ? 'Load range' : 'Refresh'}</span>
        </button>

        {rangeMode === 'custom' && (
          <p className="text-xs text-gray-500 pb-2">
            Pick your dates, then press Load range.
          </p>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Error loading data</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      )}

      {/* Data Table */}
      {!loading && !error && data && data.rows.length > 0 && (
        <div
          className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col"
          style={{ height: `max(${MIN_TABLE_HEIGHT_PX}px, calc(100vh - 400px))` }}
        >
          <div className="relative flex-1 overflow-hidden">
            {/* Scrollable container that fills available space */}
            <div
              className="overflow-auto h-full"
              style={{
                overflowX: 'auto',
                overflowY: 'auto'
              }}
            >
              <table className="divide-y divide-gray-200">
                <thead className="sticky top-0 z-20">
                  <tr>
                    {data.headers.map((header, index) => {
                      const isStickyColumn = header in STICKY_LEFT;

                      return (
                        <th
                          key={index}
                          className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 ${
                            isStickyColumn ? 'sticky' : ''
                          } ${header !== 'date' && header !== 'hour' ? 'text-right' : ''}`}
                          style={{
                            ...(isStickyColumn && {
                              left: `${STICKY_LEFT[header]}px`,
                              zIndex: 30
                            }),
                            boxShadow: header === LAST_STICKY
                              ? `${EDGE_SHADOW}, ${UNDER_SHADOW}`
                              : UNDER_SHADOW,
                            minWidth: `${STICKY_WIDTH[header] || 120}px`
                          }}
                        >
                          {header.replace(/_/g, ' ')}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.rows.map((row, rowIndex) => {
                    const dayAhead = isDayAheadRow(row);

                    return (
                      <tr key={rowIndex} className="data-row">
                        {row.map((cell, cellIndex) => {
                          const columnName = data.headers[cellIndex];
                          const isSticky = columnName in STICKY_LEFT;

                          let displayValue;

                          // Apply hour adjustment if it's the hour column and addOneHour is true
                          if (columnName === 'hour' && addOneHour && cell !== null) {
                            displayValue = (parseInt(cell, 10) + 1).toString();
                          } else {
                            displayValue = formatNumber(cell, columnName);
                          }

                          const pending = displayValue === '—';
                          // Day-ahead rows are marked on the date text alone. A
                          // tinted row background cannot work here: the global
                          // dark mode remaps every tint to a translucent rgba,
                          // which turns the frozen columns see-through.
                          const dayAheadClass =
                            dayAhead && columnName === 'date'
                              ? 'font-semibold text-indigo-600'
                              : '';

                          return (
                            <td
                              key={cellIndex}
                              className={`px-4 py-2 whitespace-nowrap text-sm bg-white ${
                                isSticky ? 'sticky' : ''
                              } ${
                                pending ? 'text-right text-gray-400' : getColumnStyles(columnName)
                              } ${dayAheadClass}`}
                              style={{
                                ...(isSticky && {
                                  left: `${STICKY_LEFT[columnName]}px`,
                                  zIndex: 10,
                                  ...(columnName === LAST_STICKY && { boxShadow: EDGE_SHADOW })
                                })
                              }}
                            >
                              {displayValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Table Footer */}
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex-shrink-0">
            <p className="text-sm text-gray-600 text-center">
              Showing {data.rows.length} rows · {data.headers.length} columns
            </p>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {!loading && !error && data && data.rows.length === 0 && (
        <div className="text-center py-12">
          <Database className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No data available for the selected range</p>
        </div>
      )}
    </div>
  );
};

export default Data;
